import { messages } from '@qality/common-messages';
import { AdjoiningTestCaseInCycle, Issue } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  TestCyclesService,
  TestExecutionService,
} from '@qality/common-service';

export default function useTestCaseExecutionNavigation(testCycleId: number) {
  const navigateToTestExecution = async (
    testCaseIssue: Issue,
    testCaseInCycle: AdjoiningTestCaseInCycle
  ): Promise<void> => {
    const testExecutionDestinationId =
      testCaseInCycle.testExecutionId ??
      (await createOrGetTestExecutionId(testCaseIssue, testCaseInCycle));

    if (testExecutionDestinationId) {
      navigate(testExecutionDestinationId);
    }
  };

  const createOrGetTestExecutionId = async (
    testCaseIssue: Issue,
    testCaseInCycle: AdjoiningTestCaseInCycle
  ): Promise<number> => {
    try {
      const testExecution = await TestExecutionService.createTestExecution(
        testCaseIssue.fields.summary,
        testCaseInCycle.testCaseId.toString(),
        testCaseInCycle.testCaseInCycleId
      );
      return testExecution.id;
    } catch (e) {
      if (e?.response?.status === 409) {
        return getExecutionIdInCaseConcurrentExecutionError(
          testCaseInCycle.testCaseInCycleId
        );
      }

      // We do not have access to testCycleId in the next scope
      // That's why we pass it in identifier.
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.executeTestCase.message'],
        undefined,
        { ['cycleNavigationError_' + testCycleId]: 'Return to test cycle page' }
      );

      StorageOfUniqueServices.get('AP').events.on('flag.action', (event) => {
        if (event.actionIdentifier.startsWith('cycleNavigationError_')) {
          // Here is the place from the comment above. We just take the id and use it.
          const testCycleIdActual = event.actionIdentifier.split('_')[1];

          StorageOfUniqueServices.get('AP').navigator.go('site', {
            absoluteUrl:
              StorageOfUniqueServices.get('URLService').getTestCycleUrl(
                testCycleIdActual
              ),
          });
        }
      });
    }
  };

  const getExecutionIdInCaseConcurrentExecutionError = async (
    testCaseInCycleId: number
  ): Promise<number> => {
    try {
      const testCaseInCycleNew = await TestCyclesService.fetchTestCaseInCycle(
        testCaseInCycleId,
        testCycleId
      );
      return testCaseInCycleNew.testExecution.id;
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.executeTestCase.message']
      );
    }
  };

  const navigate = (testExecutionId: number): void => {
    const testCaseExecutionUrl =
      StorageOfUniqueServices.get('URLService').getTestCaseExecutionUrl(
        testExecutionId
      );

    StorageOfUniqueServices.get('AP').navigator.go('site', {
      absoluteUrl: testCaseExecutionUrl,
    });
  };

  return { navigateToTestExecution };
}
